/**
 * NOTAS FISCAIS: AS REGRAS DA ESCRITA (10/10/2026, medição do item 11). Funções puras que a FiscalTab usa e os
 * testes cobram dos dois lados.
 */
import type { PeriodFilter } from '../components/PeriodSelector';
import { formatMonthLabel, formatPeriodLabel } from './formatters';

/** Valor sem fonte é "—", nunca R$ 0,00 (design/textos). */
const reais = (v: any) => { const n = parseFloat(String(v)); return Number.isFinite(n) ? `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'R$ —'; };

const ehTransmissivel = (d: any) => d?.status === 'Rascunho' || d?.status === 'Erro';

/** O nome do recorte, para a frase "não carregou <período>". */
export function rotuloDoRecorte(f: PeriodFilter): string {
  return f.mode === 'dateRange' ? formatPeriodLabel(f.startDate, f.endDate) : formatMonthLabel(f.months);
}

/**
 * D3: o lote transmite o que está NA TELA e selecionado, e o botão conta esse mesmo conjunto. Antes o envio saía de
 * `documents` (as linhas escondidas pela busca iam junto) e o botão contava `selectedIds` (ids que nem estavam mais
 * carregados), e o número do botão não era o que ia.
 */
export function notasParaTransmitir(visiveis: any[], selecionadas: number[]): any[] {
  const sel = new Set(selecionadas);
  return visiveis.filter((d) => sel.has(d.id) && ehTransmissivel(d));
}

/**
 * D5: o que o servidor sabe recortar. A planilha aceita mês ou lista de meses (o `findAll` separa por vírgula), não
 * datas: sem mês ela saía com a base INTEIRA. O ZIP de XMLs aceita UM mês: com vários, procurava o texto
 * "2026-08,2026-09" e dizia "Nenhum documento com XML"; por datas, baixava o mês corrente. Devolve o motivo da trava,
 * ou null quando pode.
 */
export function exportacaoDoPeriodo(f: PeriodFilter): { excel: string | null; zip: string | null; mesDoZip: string | null } {
  if (f.mode === 'dateRange') {
    const porDatas = 'Por datas o servidor não recorta as exportações: escolha meses no período para exportar.';
    return { excel: porDatas, zip: porDatas, mesDoZip: null };
  }
  if (f.months.length === 1) return { excel: null, zip: null, mesDoZip: f.months[0] };
  return { excel: null, zip: 'O ZIP de XMLs sai de um mês por vez: escolha um mês no período.', mesDoZip: null };
}

/** A declaração do MHemann é de UM mês: com vários meses ou por datas, a tela não escolhe por quem lê. */
export function mesDaDeclaracao(f: PeriodFilter): { mes: string | null; motivo: string | null } {
  if (f.mode === 'month' && f.months.length === 1) return { mes: f.months[0], motivo: null };
  return { mes: null, motivo: 'A declaração do MHemann é de um mês: escolha um mês no período.' };
}

const rotuloDoTipo = (t: string) => (t === 'nfse' ? 'NFS-e' : t === 'nfcom' ? 'NFCom' : String(t || '').toUpperCase());
const competencia = (ref?: string | null) => (ref && /^\d{4}-\d{2}$/.test(ref) ? `${ref.slice(5)}/${ref.slice(0, 4)}` : ref || '—');

/**
 * D2: o cancelamento é irreversível na prefeitura/SEFAZ, e o modal dizia só "Cancelar NFS-e #número?". Agora diz
 * qual nota (número, cliente, valor, competência) e o que acontece com a fatura: o servidor só limpa o status fiscal
 * dela (fiscal-document.service syncInvoiceFiscalStatus). A fatura continua como está, e nenhuma nota nova nasce
 * sozinha.
 */
export function confirmacaoDeCancelarNota(doc: any): { titulo: string; linhas: string[] } {
  const cliente = doc?.client?.name?.trim() || doc?.client?.companyName || 'cliente não identificado';
  const numero = doc?.documentNumber || `#${doc?.id}`;
  const valor = doc?.totalValue ?? doc?.invoice?.totalValue;
  const linhas = [
    `${cliente} · ${reais(valor)} · competência ${competencia(doc?.referenceMonth || doc?.invoice?.referenceMonth)}.`,
  ];
  if (doc?.invoice?.code) {
    linhas.push(
      `A fatura ${doc.invoice.code} continua ${doc.invoice.status || 'como está'} e fica SEM nota: o boleto dela, se houver, segue valendo. ` +
      'Nenhuma nota nova é gerada sozinha; para emitir outra, use "Regerar nota fiscal" na fatura.',
    );
  } else if (doc?.invoiceGroup?.code) {
    linhas.push(`O grupo ${doc.invoiceGroup.code} fica SEM nota. Nenhuma nota nova é gerada sozinha.`);
  } else {
    linhas.push('Esta nota não está ligada a uma fatura carregada nesta tela.');
  }
  linhas.push('O cancelamento vai para a prefeitura/SEFAZ e não pode ser desfeito.');
  return { titulo: `Cancelar ${rotuloDoTipo(doc?.type)} ${numero}?`, linhas };
}

const docDoCertificado = (c: any) => {
  const d = String(c?.cnpj || '');
  if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return d || 'sem documento';
};
const validadeDoCertificado = (c: any) => {
  const v = String(c?.validUntil || c?.validTo || c?.expiresAt || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v.split('-').reverse().join('/') : '—';
};
const descreverCertificado = (c: any) => `${c?.name || c?.subject || 'sem nome'} (${docDoCertificado(c)}, validade ${validadeDoCertificado(c)})`;

/**
 * D4, o upload: o servidor DESATIVA o certificado ativo do mesmo CNPJ/CPF do arquivo (certificate.service), e o modal
 * não dizia. O CNPJ do arquivo só se sabe depois de lido, então o aviso nomeia os ativos que podem sair.
 */
export function avisoDoUpload(certificados: any[]): string | null {
  const ativos = certificados.filter((c) => c?.isActive);
  if (ativos.length === 0) return null;
  return `Ao enviar, o certificado ativo do MESMO CNPJ/CPF do arquivo é desativado, e o novo passa a valer: ${ativos.map(descreverCertificado).join('; ')}.`;
}

/** D4, o excluir: nomeia o certificado e diz se é o ativo da empresa (o que assina as notas). */
export function confirmacaoDeExcluirCertificado(c: any): string {
  const ativoDaEmpresa = c?.isActive && c?.type !== 'pessoal';
  // O primeiro parágrafo vira o TÍTULO do confirm, que a casca trunca numa linha: só o nome vai nele.
  return [
    `Excluir o certificado ${c?.name || c?.subject || 'sem nome'}?`,
    `${docDoCertificado(c)}, validade ${validadeDoCertificado(c)}.`,
    ativoDaEmpresa
      ? 'É o certificado ATIVO da empresa: sem outro ativo do mesmo CNPJ, a emissão de NFS-e e NFCom para.'
      : c?.isActive ? 'É um certificado pessoal ativo.' : 'Ele não está ativo.',
    'O arquivo do certificado é apagado do armazenamento.',
  ].join('\n\n');
}
